import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import { Print, FileDownload, SaveAlt } from '@mui/icons-material';
import { gridVisibleSortedRowIdsSelector, useGridApiContext } from '@mui/x-data-grid';

export default function StyledExportButton({ buttonSx, ...rest }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const apiRef = useGridApiContext();
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handlePrint = () => {
        apiRef.current.exportDataAsPrint();
        handleClose();
    };

    const handleCsvExport = () => {
        const visibleColumns = apiRef.current.getVisibleColumns().filter((col) => col.field !== '__check__' && !col.disableExport);

        const sortedRowIds = gridVisibleSortedRowIdsSelector(apiRef);

        const headers = visibleColumns.map((col) => col.headerName || col.field).join(',');

        const csvRows = sortedRowIds.map((id) => {
            const row = apiRef.current.getRow(id);
            return visibleColumns
                .map((col) => {
                    let value = row[col.field];
                    if (col.valueGetter) {
                        value = col.valueGetter({ row, field: col.field, value, api: apiRef.current });
                    }
                    if (col.valueFormatter) {
                        value = col.valueFormatter({ value, field: col.field, api: apiRef.current });
                    }
                    if (value == null) return '';
                    if (typeof value === 'number') return value;
                    if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                        return `"${value.replace(/"/g, '""')}"`;
                    }
                    return value;
                })
                .join(',');
        });

        const csvContent = [headers, ...csvRows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const sanitizedTitle = document.title
            .replace(/[^a-z0-9]/gi, '_')
            .replace(/_+/g, '_')
            .toLowerCase();
        link.href = url;
        link.download = `${sanitizedTitle}.csv`;
        link.click();
        URL.revokeObjectURL(url);
        handleClose();
    };

    return (
        <>
            <IconButton
                size="small"
                onClick={handleClick}
                sx={{
                    fontSize: 0,
                    minWidth: 0,
                    padding: '4px',
                    borderRadius: '4px',
                    border: 'none',
                    '& .MuiSvgIcon-root': {
                        fontSize: '2.1rem',
                    },
                    '&:hover': {
                        backgroundColor: 'action.hover',
                    },
                    ...buttonSx,
                }}
                {...rest}
            >
                <SaveAlt />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
                <MenuItem onClick={handleCsvExport}>
                    <ListItemIcon>
                        <FileDownload fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Download as CSV</ListItemText>
                </MenuItem>
                <MenuItem onClick={handlePrint}>
                    <ListItemIcon>
                        <Print fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Print</ListItemText>
                </MenuItem>
            </Menu>
        </>
    );
}
